import React from 'react';
import { Card, Typography, Button, Tag, Divider } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { formatDate } from '../../../../helpers/date';
import './voucherCard.css';

interface VoucherCardProps {
  voucher: {
    voucherNumber: string;
    intPkVoucherID: string | number;
    voucherStatus: string;
    dateCreated: string;
    voucherUsedDate: string | null;
    userMacAddress: string | null;
    expiryDate: string | null;
    formattedTotalUploadedBytes?: string;
    formattedTotalDownloadedBytes?: string;
    formattedTotalUptime?: string;
    batchSpeedDown?: number;
    batchSpeedUp?: number;
    batchSpeedUnit?: 'K' | 'M' | 'G';
    batchMaxDataVolumeLimit?: number | null;
    batchMaxDataVolumeUnit?: 'K' | 'M' | 'G' | null;
    batchPackageDurationEnum?: 'HOURS' | 'DAYS';
    batchPackageDurationDays?: number;
    batchPackageDurationHours?: number;
  };
  onResetVoucher?: (voucherId: number | string) => void;
  canResetVoucher?: boolean;
}

const VoucherCard: React.FC<VoucherCardProps> = ({ voucher, onResetVoucher, canResetVoucher = false }) => {
  const handleCopy = (voucherNumber: string) => {
    navigator.clipboard
      .writeText(voucherNumber)
      .then(() => {
        message.success('Voucher number copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy voucher number');
      });
  };

  const isUsed = voucher.voucherStatus === 'USED';

  // Format speed display
  const getSpeedDisplay = (speedDown?: number, speedUp?: number, speedUnit?: 'K' | 'M' | 'G') => {
    if (!speedDown || !speedUp || !speedUnit) return 'N/A';
    const unitLabel = speedUnit === 'K' ? 'Kbps' : speedUnit === 'M' ? 'Mbps' : 'Gbps';
    return `${speedDown} / ${speedUp} ${unitLabel}`;
  };

  // Format data volume display
  const getDataVolumeDisplay = () => {
    if (!voucher.batchMaxDataVolumeLimit || !voucher.batchMaxDataVolumeUnit) {
      return 'Unlimited';
    }
    const unitLabel =
      voucher.batchMaxDataVolumeUnit === 'K' ? 'KB' : voucher.batchMaxDataVolumeUnit === 'M' ? 'MB' : 'GB';
    return `${voucher.batchMaxDataVolumeLimit} ${unitLabel}`;
  };

  // Format duration display
  const getDurationDisplay = () => {
    const enumValue = voucher.batchPackageDurationEnum;
    const days = voucher.batchPackageDurationDays;
    const hours = voucher.batchPackageDurationHours;

    // Debug: log to see what data we're receiving
    if (!enumValue && days === undefined && hours === undefined) {
      console.log('Duration data missing for voucher:', voucher.voucherNumber, {
        enumValue,
        days,
        hours,
        batchData: {
          batchPackageDurationEnum: voucher.batchPackageDurationEnum,
          batchPackageDurationDays: voucher.batchPackageDurationDays,
          batchPackageDurationHours: voucher.batchPackageDurationHours,
        },
      });
    }

    // Normalize enum value to uppercase for comparison
    const normalizedEnum = enumValue ? String(enumValue).toUpperCase() : null;

    // Check if enum is DAYS - use days value (check for null/undefined, not falsy, since 0 is valid)
    if (normalizedEnum === 'DAYS') {
      if (days !== null && days !== undefined) {
        return `${days} ${days === 1 ? 'day' : 'days'}`;
      }
    }

    // Check if enum is HOURS - use hours value
    if (normalizedEnum === 'HOURS') {
      if (hours !== null && hours !== undefined) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      }
    }

    // Fallback: if no enum but we have values, use whichever is greater than 0
    if (days !== null && days !== undefined && days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }

    if (hours !== null && hours !== undefined && hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    return 'N/A';
  };

  return (
    <Card className="voucher-card" hoverable>
      {/* Header Section */}
      <div className="voucher-card-header">
        <div className="voucher-card-status">
          <Tag color={isUsed ? 'red' : 'green'} className="voucher-status-tag">
            {isUsed ? 'Used' : 'Unused'}
          </Tag>
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Voucher Code Section - Most Prominent */}
      <div className="voucher-code-section">
        <Typography.Text className="voucher-code-label">Voucher Code</Typography.Text>
        <div className="voucher-code-box">
          <Typography.Text
            className="voucher-code-value"
            copyable={{
              text: voucher.voucherNumber,
              onCopy: () => handleCopy(voucher.voucherNumber),
            }}
          >
            {voucher.voucherNumber}
          </Typography.Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(voucher.voucherNumber)}
            className="voucher-copy-btn"
            title="Copy voucher number"
          />
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Details Section */}
      <div className="voucher-details-section">
        {/* Speed and Data Limit from Batch */}
        {(voucher.batchSpeedDown || voucher.batchSpeedUp) && (
          <div className="voucher-detail-row">
            <Typography.Text className="voucher-detail-label">Speed:</Typography.Text>
            <Typography.Text className="voucher-detail-value" style={{ fontWeight: 600, color: '#1890ff' }}>
              {getSpeedDisplay(voucher.batchSpeedDown, voucher.batchSpeedUp, voucher.batchSpeedUnit)}
            </Typography.Text>
          </div>
        )}

        <div className="voucher-detail-row">
          <Typography.Text className="voucher-detail-label">Data Limit:</Typography.Text>
          <Typography.Text className="voucher-detail-value" style={{ fontWeight: 600, color: '#1890ff' }}>
            {getDataVolumeDisplay()}
          </Typography.Text>
        </div>

        <div className="voucher-detail-row">
          <Typography.Text className="voucher-detail-label">Duration:</Typography.Text>
          <Typography.Text className="voucher-detail-value" style={{ fontWeight: 600, color: '#1890ff' }}>
            {getDurationDisplay()}
          </Typography.Text>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <div className="voucher-detail-row">
          <Typography.Text className="voucher-detail-label">Created:</Typography.Text>
          <Typography.Text className="voucher-detail-value">{formatDate(voucher.dateCreated)}</Typography.Text>
        </div>

        {voucher.voucherUsedDate && (
          <div className="voucher-detail-row">
            <Typography.Text className="voucher-detail-label">Used Date:</Typography.Text>
            <Typography.Text className="voucher-detail-value">
              {voucher.voucherUsedDate ? formatDate(voucher.voucherUsedDate) : '-'}
            </Typography.Text>
          </div>
        )}

        {voucher.expiryDate && (
          <div className="voucher-detail-row">
            <Typography.Text className="voucher-detail-label">Expiry Date:</Typography.Text>
            <Typography.Text className="voucher-detail-value">
              {voucher.expiryDate ? formatDate(voucher.expiryDate) : '-'}
            </Typography.Text>
          </div>
        )}

        {voucher.userMacAddress && (
          <div className="voucher-detail-row">
            <Typography.Text className="voucher-detail-label">MAC Address:</Typography.Text>
            <Typography.Text className="voucher-detail-value" style={{ fontFamily: 'monospace' }}>
              {voucher.userMacAddress}
            </Typography.Text>
          </div>
        )}

        <Divider style={{ margin: '8px 0' }} />

        {/* Data Usage Section */}
        <div className="voucher-usage-section">
          <div className="voucher-usage-item">
            <Typography.Text className="voucher-usage-label">Downloaded</Typography.Text>
            <Typography.Text className="voucher-usage-value">
              {voucher.formattedTotalDownloadedBytes || '0 B'}
            </Typography.Text>
          </div>
          <div className="voucher-usage-item">
            <Typography.Text className="voucher-usage-label">Uploaded</Typography.Text>
            <Typography.Text className="voucher-usage-value">
              {voucher.formattedTotalUploadedBytes || '0 B'}
            </Typography.Text>
          </div>
        </div>

        {voucher.formattedTotalUptime && (
          <div className="voucher-detail-row" style={{ marginTop: '8px' }}>
            <Typography.Text className="voucher-detail-label">Uptime:</Typography.Text>
            <Typography.Text className="voucher-detail-value">{voucher.formattedTotalUptime}</Typography.Text>
          </div>
        )}
      </div>

      {/* Actions Section */}
      {canResetVoucher && onResetVoucher && isUsed && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div className="voucher-actions-section">
            <Button
              type="primary"
              danger
              icon={<ReloadOutlined />}
              onClick={() => onResetVoucher(voucher.intPkVoucherID)}
              block
              size="small"
            >
              Reset to Reuse
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default VoucherCard;
